/**
 * Graph Mapping Utilities - MA-ARCH-164
 * 
 * Pure functions for transforming funnel graph data into visualization model.
 * Extracted from FunnelVisualization.jsx for testability.
 */

/**
 * Check if a node is truly terminal (has is_terminal flag).
 * @param {Object} node - The node object
 * @returns {boolean}
 */
export function isNodeTrulyTerminal(node) {
  return node?.content?.is_terminal === true;
}

/**
 * Get effective next steps for a node.
 * 
 * MA-FUNNEL-ROUTING-050A: Reads ONLY canonical edges.
 * No implicit routing, no order-based fallbacks.
 * 
 * @param {string} nodeId - The node ID
 * @param {Object} node - The node object
 * @param {Map} outgoingEdges - Map of nodeId -> edges
 * @param {Map} nodeMap - Map of nodeId -> node
 * @param {Array} allNodeIds - Ordered array of all node IDs
 * @returns {Array<string>} - Array of next step node IDs
 */
export function getEffectiveNextSteps(nodeId, node, outgoingEdges, nodeMap, allNodeIds) {
  // MA-FUNNEL-GRAPH-045: Terminal nodes are graph sinks — never produce next steps
  if (isNodeTrulyTerminal(node)) return [];
  
  const nextSteps = new Set();
  
  const outgoing = outgoingEdges.get(nodeId) || [];
  
  // MA-FUNNEL-ROUTING-052: Canonical edges are the ONLY routing truth.
  // No __continue__, no __exit_*, no fallbacks, no order-based resolution.
  // Every edge target must be a real node ID.
  
  for (const edge of outgoing) {
    const targetId = edge.target_node_id;
    if (targetId && nodeMap.has(targetId)) {
      nextSteps.add(targetId);
    }
  }
  
  return Array.from(nextSteps);
}

/**
 * Build the graph data structure from nodes and edges.
 * @param {Array} nodes - Array of node objects
 * @param {Array} edges - Array of edge objects
 * @param {string} entryNodeId - The entry node ID
 * @returns {Object} - Graph data structure
 */
export function buildGraphData(nodes, edges, entryNodeId) {
  const nodeMap = new Map(nodes.map(n => [n.id, n]));
  const allNodeIds = nodes.map(n => n.id);
  
  // Filter valid edges — MA-FUNNEL-ROUTING-052: Only real node IDs allowed. No aliases.
  const validEdges = edges.filter(edge => {
    const sourceId = edge.source_node_id;
    const targetId = edge.target_node_id;
    if (!sourceId || !targetId) return false;
    if (!nodeMap.has(sourceId)) return false;
    if (targetId.startsWith("__")) return false;  // Drop ALL alias targets
    if (!nodeMap.has(targetId)) return false;
    if (sourceId === targetId) return false;
    return true;
  });
  
  // Build edge maps
  const outgoingEdges = new Map();
  const incomingEdges = new Map();
  
  for (const edge of validEdges) {
    const sourceId = edge.source_node_id;
    const targetId = edge.target_node_id;
    
    if (!outgoingEdges.has(sourceId)) outgoingEdges.set(sourceId, []);
    outgoingEdges.get(sourceId).push({
      ...edge,
      targetNode: nodeMap.get(targetId) || null,
    });
    
    if (targetId && nodeMap.has(targetId)) {
      if (!incomingEdges.has(targetId)) incomingEdges.set(targetId, []);
      incomingEdges.get(targetId).push(sourceId);
    }
  }
  
  // Find terminal and branching nodes
  const terminalNodes = new Set();
  for (const node of nodes) {
    if (isNodeTrulyTerminal(node)) terminalNodes.add(node.id);
  }
  
  const branchingNodes = new Set();
  for (const node of nodes) {
    const effectiveNextSteps = getEffectiveNextSteps(node.id, node, outgoingEdges, nodeMap, allNodeIds);
    if (effectiveNextSteps.length > 1) branchingNodes.add(node.id);
  }
  
  // Calculate levels (BFS from entry)
  const levels = new Map();
  const visited = new Set();
  const queue = [[entryNodeId || nodes[0]?.id, 0]];
  
  while (queue.length > 0) {
    const [nodeId, level] = queue.shift();
    if (!nodeId || visited.has(nodeId)) continue;
    visited.add(nodeId);
    levels.set(nodeId, level);
    const node = nodeMap.get(nodeId);
    const nextSteps = getEffectiveNextSteps(nodeId, node, outgoingEdges, nodeMap, allNodeIds);
    for (const nextId of nextSteps) {
      if (!visited.has(nextId)) queue.push([nextId, level + 1]);
    }
  }
  
  // Assign levels to unvisited nodes
  for (const node of nodes) {
    if (!levels.has(node.id)) {
      const maxLevel = Math.max(...Array.from(levels.values()), -1);
      levels.set(node.id, maxLevel + 1);
    }
  }
  
  // Group nodes by level
  const levelGroups = new Map();
  for (const [nodeId, level] of levels) {
    if (!levelGroups.has(level)) levelGroups.set(level, []);
    levelGroups.get(level).push(nodeId);
  }
  
  // Build visual edges
  const visualEdges = [];
  for (const node of nodes) {
    const nodeId = node.id;
    const nextSteps = getEffectiveNextSteps(nodeId, node, outgoingEdges, nodeMap, allNodeIds);
    for (const targetId of nextSteps) {
      const existingEdge = validEdges.find(
        e => e.source_node_id === nodeId && e.target_node_id === targetId
      );
      if (existingEdge) {
        visualEdges.push(existingEdge);
      } else {
        visualEdges.push({
          source_node_id: nodeId,
          target_node_id: targetId,
          condition: existingEdge?.condition || null,
          label: nextSteps.length === 1 ? null : existingEdge?.label,
        });
      }
    }
  }
  
  // Deduplicate edges
  const edgeKeys = new Set();
  const dedupedEdges = visualEdges.filter(edge => {
    const key = `${edge.source_node_id}->${edge.target_node_id}`;
    if (edgeKeys.has(key)) return false;
    edgeKeys.add(key);
    return true;
  });
  
  // MA-FUNNEL-GRAPH-045: Defensive assertion — terminal nodes must never appear as edge sources
  if (process.env.NODE_ENV !== 'production') {
    for (const edge of dedupedEdges) {
      if (terminalNodes.has(edge.source_node_id)) {
        console.warn(
          `[GRAPH INTEGRITY] Terminal node "${edge.source_node_id}" has outgoing visual edge → "${edge.target_node_id}". ` +
          `This violates the graph invariant: END nodes must be sinks.`
        );
      }
    }
  }
  
  return {
    nodeMap,
    allNodeIds,
    outgoingEdges,
    incomingEdges,
    terminalNodes,
    branchingNodes,
    levels,
    levelGroups,
    visualEdges: dedupedEdges,
  };
}

/**
 * Calculate node positions for visualization.
 * @param {Object} graphData - The graph data structure
 * @param {Object} dimensions - Node dimensions constants
 * @returns {Object} - Positions and total dimensions
 */
export function calculateNodePositions(graphData, dimensions) {
  const { levelGroups, incomingEdges } = graphData;
  const { NODE_WIDTH, NODE_HEIGHT, HORIZONTAL_GAP, VERTICAL_GAP } = dimensions;
  
  const positions = new Map();
  const TOP_OFFSET = 40;
  const SIDE_PADDING = 40;
  const MIN_WIDTH = 400;
  
  const sortedLevels = Array.from(levelGroups.keys()).sort((a, b) => a - b);
  
  // Calculate the natural max width based on content
  let maxWidth = 0;
  for (const level of sortedLevels) {
    const nodesAtLevel = levelGroups.get(level);
    const levelWidth = nodesAtLevel.length * NODE_WIDTH + (nodesAtLevel.length - 1) * HORIZONTAL_GAP;
    maxWidth = Math.max(maxWidth, levelWidth);
  }
  
  // Calculate the actual total width (with minimum)
  const naturalWidth = maxWidth + SIDE_PADDING * 2;
  const totalWidth = Math.max(naturalWidth, MIN_WIDTH);
  
  // Calculate the centering offset
  const centeringOffset = (totalWidth - naturalWidth) / 2;
  
  for (const level of sortedLevels) {
    const nodesAtLevel = levelGroups.get(level);
    const levelWidth = nodesAtLevel.length * NODE_WIDTH + (nodesAtLevel.length - 1) * HORIZONTAL_GAP;
    const startX = (maxWidth - levelWidth) / 2 + SIDE_PADDING + centeringOffset;
    
    // Sort nodes by parent position
    const sortedNodes = [...nodesAtLevel].sort((a, b) => {
      const aParents = incomingEdges.get(a) || [];
      const bParents = incomingEdges.get(b) || [];
      const aPos = aParents.length > 0 ? positions.get(aParents[0]) : null;
      const bPos = bParents.length > 0 ? positions.get(bParents[0]) : null;
      if (!aPos && !bPos) return 0;
      if (!aPos) return 1;
      if (!bPos) return -1;
      return aPos.x - bPos.x;
    });
    
    sortedNodes.forEach((nodeId, index) => {
      positions.set(nodeId, {
        x: startX + index * (NODE_WIDTH + HORIZONTAL_GAP),
        y: level * (NODE_HEIGHT + VERTICAL_GAP) + TOP_OFFSET,
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        level: level,
      });
    });
  }
  
  return {
    positions,
    totalWidth,
    totalHeight: sortedLevels.length * (NODE_HEIGHT + VERTICAL_GAP) + TOP_OFFSET + 80,
  };
}

export default {
  isNodeTrulyTerminal,
  getEffectiveNextSteps,
  buildGraphData,
  calculateNodePositions,
};
